class SchoolHeroModal extends HTMLElement {
  constructor() {
    super();
    this.modalId = this.getAttribute('id');
    this.triggerSelector = `[data-modal-trigger="${this.modalId}"]`;
    this.closeSelector = `[data-modal-close="${this.modalId}"]`;
    this.isOpen = false;
    
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupAccessibility();
  }

  setupEventListeners() {
    // Open modal when trigger is clicked
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(this.triggerSelector);
      if (trigger && !this.isOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.openModal();
      }
    });

    // Close modal when close button is clicked
    this.addEventListener('click', (event) => {
      if (event.target.matches(this.closeSelector) || 
          event.target.closest(this.closeSelector)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
        return false;
      }
    });

    // Close modal when clicking on overlay
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('school-hero-modal__overlay')) {
        event.stopPropagation();
        this.closeModal();
      }
    });

    // Close modal with Escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
    });

    // Trap focus within modal when open
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && this.isOpen) {
        this.trapFocus(event);
      }
    });
  }

  setupAccessibility() {
    // Set initial ARIA attributes
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('role', 'dialog');
    
    // Find the modal content wrapper
    const modalContent = this.querySelector('.school-hero-modal__content');
    if (modalContent) {
      modalContent.setAttribute('aria-modal', 'true');
    }
  }

  openModal() {
    this.isOpen = true;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Focus the modal
    this.focus();
    
    // Focus the first focusable element
    const firstFocusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      firstFocusable.focus();
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('modal:opened', {
      detail: { modalId: this.modalId }
    }));
  }

  closeModal() {
    if (!this.isOpen) return; // Already closed
    
    this.isOpen = false;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Focus the trigger button after a short delay to prevent immediate reopening
    const trigger = document.querySelector(this.triggerSelector);
    if (trigger) {
      setTimeout(() => {
        trigger.focus();
      }, 100);
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('modal:closed', {
      detail: { modalId: this.modalId }
    }));
  }

  trapFocus(event) {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        event.preventDefault();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        event.preventDefault();
      }
    }
  }
}

// Register the custom element only if not already defined
if (!customElements.get('school-hero-modal')) {
  customElements.define('school-hero-modal', SchoolHeroModal);
}

// Initialize existing modals when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const modals = document.querySelectorAll('school-hero-modal');
  modals.forEach(modal => {
    // Modal is already initialized by the custom element constructor
  });
});

// Handle dynamic content (if modals are added after page load)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SCHOOL-HERO-MODAL') {
        // Modal will be automatically initialized by the custom element constructor
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});
